import { from, type Observable, of } from "rxjs";
import { type AjaxResponse, ajax } from "rxjs/ajax";
import { concatMap, mergeMap, toArray } from "rxjs/operators";
import type {
  Label,
  MyWorkspaceMetadata,
  ProjectResponse,
  ProjectUsersResponse,
  ProjectWithUserRole,
  UserWorkspaceTonnageResponse,
  WorkspaceRole,
  WorkspaceRoles,
} from "../../../state/workspaces/types";
import type {
  HiddenWorkspacesPagingRequest,
  ProjectWorkspacesRequest,
  WorkspacesPagingRequest,
  WorkspacesPagingResult,
} from "../../../state/workspaces/workspaces.actions";
import { patchJSON, postJSON, putJSON } from "../../dependencies/ajaxRequests";
import type { GlobalAccessType } from "../global.access";
import {
  assertMyWorkspaceResponse,
  type CreateWorkspaceRequestMetadata,
  type DuplicateWorkspaceRequestMetadata,
  stripInvalidWorkspaces,
} from "./workspaces.types";

const EmptyMyWorkspaceMetadataArray: MyWorkspaceMetadata[] = [];

type BaseRequest<T extends object = object> = {
  origin: string;
  token: string;
} & T;

interface UserId {
  userId: string;
}

// DASHBOARD ENDPOINTS

function getDashboardWorkspacesPaged({
  origin,
  token,
  userId,
  includeHidden,
  labels,
  limit,
  name,
  offset,
}: BaseRequest<
  WorkspacesPagingRequest & UserId
>): Observable<WorkspacesPagingResult> {
  const searchParams = new URLSearchParams();

  if (includeHidden) searchParams.set("includeHidden", "true");
  if (labels) searchParams.set("labels", labels.toLowerCase());
  if (limit) searchParams.set("limit", limit.toString());
  if (name) searchParams.set("name", name.toLowerCase());
  if (offset) searchParams.set("offset", offset.toString());

  const url = `${origin}/api/v3/dashboard/${userId}?${searchParams.toString()}`;

  return ajax
    .getJSON<WorkspacesPagingResult>(url, {
      Authorization: `Bearer ${token}`,
    })
    .pipe(
      mergeMap((response) => {
        assertMyWorkspaceResponse(response);
        const { workspaces, offset } = stripInvalidWorkspaces(response);

        return workspaces?.length
          ? of({ workspaces, offset })
          : of({
              workspaces: EmptyMyWorkspaceMetadataArray,
              offset: null,
            });
      }),
    );
}

function getDashboardHiddenWorkspacesPaged({
  origin,
  token,
  userId,
  labels,
  limit,
  name,
  offset,
}: BaseRequest<
  HiddenWorkspacesPagingRequest & UserId
>): Observable<WorkspacesPagingResult> {
  const searchParams = new URLSearchParams();

  if (labels) searchParams.set("labels", labels.toLowerCase());
  if (limit) searchParams.set("limit", limit.toString());
  if (name) searchParams.set("name", name.toLowerCase());
  if (offset) searchParams.set("offset", offset.toString());

  const url = `${origin}/api/v3/dashboard/${userId}/hidden?${searchParams.toString()}`;

  return ajax
    .getJSON<WorkspacesPagingResult>(url, {
      Authorization: `Bearer ${token}`,
    })
    .pipe(
      mergeMap((response) => {
        assertMyWorkspaceResponse(response);
        const { workspaces, offset } = stripInvalidWorkspaces(response);

        return workspaces?.length
          ? of({ workspaces, offset })
          : of({
              workspaces: EmptyMyWorkspaceMetadataArray,
              offset: null,
            });
      }),
    );
}

/** Remove a workspace from the users dashboard permanently (forget) */
function removeFromMyWorkspaces(
  origin: string,
  token: string,
  userId: string,
  workspaceId: string,
) {
  const url = `${origin}/api/v3/dashboard/${userId}/${workspaceId}`;
  return ajax.delete(url, { Authorization: `Bearer ${token}` });
}

/**
 * Get all unique labels from the user's workspaces.
 */
function getDashboardLabels({ origin, token, userId }: BaseRequest<UserId>) {
  const url = `${origin}/api/v3/dashboard/${userId}/labels`;

  return ajax.getJSON<Label[]>(url, { Authorization: `Bearer ${token}` });
}

function setDashboardWorkspaceVisibility({
  isHidden,
  origin,
  token,
  userId,
  workspaceId,
}: BaseRequest<
  UserId & { isHidden: boolean; workspaceId: string }
>): Observable<AjaxResponse> {
  const url = `${origin}/api/v3/dashboard/${userId}/${workspaceId}/visibility?isHidden=${isHidden}`;
  return ajax.patch(url, undefined, { Authorization: `Bearer ${token}` });
}

// PROJECTS ENDPOINTS

function getProjectWorkspaces({
  origin,
  projectId,
  token,
  labels,
  name,
}: BaseRequest<ProjectWorkspacesRequest>): Observable<MyWorkspaceMetadata[]> {
  const searchParams = new URLSearchParams();

  if (labels) searchParams.set("labels", labels.toLowerCase());
  if (name) searchParams.set("name", name.toLowerCase());

  const url = `${origin}/api/v3/projects/${projectId}/workspaces?${searchParams.toString()}`;

  return ajax.getJSON<MyWorkspaceMetadata[]>(url, {
    Authorization: `Bearer ${token}`,
  });
}

/**
 * Gets all project's collaborators
 * @returns an array of ProjectUsersResponse
 */
function getProjectUsers(
  origin: string,
  token: string,
  containerId?: string | null,
): Observable<ProjectUsersResponse[]> {
  const url = `${origin}/api/v3/projects/${containerId}/users`;

  return ajax.getJSON<ProjectUsersResponse[]>(url, {
    Authorization: `Bearer ${token}`,
  });
}

/** Add workspaces to the specified project. */
function putProjectsProjectWorkspaces({
  projectId,
  origin,
  token,
  workspaceIds,
}: BaseRequest<{
  projectId: string;
  workspaceIds: string[];
}>): Observable<void> {
  const url = `${origin}/api/v3/projects/${projectId}/workspaces`;

  return putJSON<string[], undefined>(
    url,
    workspaceIds,
    {
      Authorization: `Bearer ${token}`,
    },
    true,
  );
}

/** Delete workspace from the specified project. */
function deleteProjectsProjectWorkspace({
  projectId,
  origin,
  token,
  workspaceId,
}: BaseRequest<{
  projectId: string;
  workspaceId: string;
}>): Observable<AjaxResponse> {
  const url = `${origin}/api/v3/projects/${projectId}/workspaces/${workspaceId}`;

  return ajax.delete(url, {
    Authorization: `Bearer ${token}`,
  });
}

/** Get all the projects the specified user has access to. */
function getProjectsMy({
  origin,
  token,
}: BaseRequest): Observable<ProjectWithUserRole[] | null> {
  const url = `${origin}/api/v3/projects/my`;

  return ajax.getJSON<ProjectWithUserRole[] | null>(url, {
    Authorization: `Bearer ${token}`,
  });
}

/** Get all the projects the specified user can assign workspaces to. */
function getProjectsMyAssignable({
  origin,
  token,
}: BaseRequest): Observable<ProjectResponse[] | null> {
  const url = `${origin}/api/v3/projects/my/assignable`;

  return ajax.getJSON<ProjectResponse[] | null>(url, {
    Authorization: `Bearer ${token}`,
  });
}

// WORKSPACE ENDPOINTS

/**
 * Gets the raw workspace metadata in the v3 document metadata format.
 * Note: This does not prepare the workspace for opening, since no password is/can be provided here.
 */
function getRawWorkspaceMetadata(
  origin: string,
  token: string,
  workspaceId: string,
): Observable<MyWorkspaceMetadata> {
  const url = `${origin}/api/v3/workspace/${workspaceId}`;
  return ajax.getJSON<MyWorkspaceMetadata>(url, {
    Authorization: `Bearer ${token}`,
  });
}

function createWorkspace(
  origin: string,
  token: string,
  workspaceDetails: CreateWorkspaceRequestMetadata,
): Observable<MyWorkspaceMetadata> {
  const url = `${origin}/api/v3/workspace`;

  return postJSON<CreateWorkspaceRequestMetadata, MyWorkspaceMetadata>(
    url,
    workspaceDetails,
    {
      Authorization: `Bearer ${token}`,
    },
  );
}

function duplicateWorkspace(
  origin: string,
  token: string,
  sourceWorkspaceId: string,
  sourcePassword: string | undefined,
  body: DuplicateWorkspaceRequestMetadata,
): Observable<MyWorkspaceMetadata> {
  const url = `${origin}/api/v3/workspace/${sourceWorkspaceId}/duplicate`;

  return postJSON<DuplicateWorkspaceRequestMetadata, MyWorkspaceMetadata>(
    url,
    body,
    {
      Authorization: `Bearer ${token}`,
      ...(sourcePassword && {
        "X-Hoylu-Workspace-Password": sourcePassword,
      }),
    },
  );
}

function upsertWorkspaceUserPermissionsOfSingleUser(
  origin: string,
  token: string,
  workspaceId: string,
  user: { userId?: string; userEmail?: string }, // either userId or userEmail must be provided
  permission: WorkspaceRole,
) {
  let url = `${origin}/api/v3/workspace/${workspaceId}/roles?role=${permission}`;
  if (user.userId) {
    url += `&userId=${user.userId}`;
  } else if (user.userEmail) {
    url += `&userEmail=${encodeURIComponent(user.userEmail)}`;
  } else {
    throw new Error("Either userId or userEmail must be provided");
  }
  return patchJSON(url, undefined, { Authorization: `Bearer ${token}` }, true);
}

function upsertWorkspaceUserPermissions(
  origin: string,
  token: string,
  workspaceId: string,
  userEmails: string[],
  permission: WorkspaceRole,
): Observable<WorkspaceRoles> {
  // NOTE: If we would have a bulk endpoint for this we could simplify this a lot (replace concatMap, toArray, switchMap with a single request)
  return from(userEmails).pipe(
    // concatMap to make sure requests are executed in order
    concatMap((userEmail) =>
      upsertWorkspaceUserPermissionsOfSingleUser(
        origin,
        token,
        workspaceId,
        { userEmail },
        permission,
      ),
    ),
    // collect all responses into an array (and most important a single observable)
    toArray(),
    mergeMap(() => getWorkspaceRoles(origin, token, workspaceId)),
  );
}

function removeUsersFromWorkspacePermissions(
  origin: string,
  token: string,
  workspaceId: string,
  userEmails: string[],
): Observable<WorkspaceRoles> {
  // use forkJoin to make sure all requests are awaited and merged into a single observable
  // NOTE: If we would have a bulk endpoint for this we could simplify this a lot (replace concatMap, toArray, switchMap with a single request)
  return from(userEmails).pipe(
    concatMap((userEmail) =>
      removeUserFromWorkspacePermissionsOfSingleUser(
        origin,
        token,
        workspaceId,
        { userEmail },
      ),
    ),
    toArray(),
    mergeMap(() => getWorkspaceRoles(origin, token, workspaceId)),
  );
}

function removeUserFromWorkspacePermissionsOfSingleUser(
  origin: string,
  token: string,
  workspaceId: string,
  user: { userId?: string; userEmail?: string }, // either userId or userEmail must be provided
) {
  let url = `${origin}/api/v3/workspace/${workspaceId}/roles?`;
  if (user.userId) {
    url += `userId=${user.userId}`;
  } else if (user.userEmail) {
    url += `userEmail=${encodeURIComponent(user.userEmail)}`;
  } else {
    throw new Error("Either userId or userEmail must be provided");
  }
  return ajax.delete(url, { Authorization: `Bearer ${token}` });
}

function getWorkspaceRoles(
  origin: string,
  token: string,
  workspaceId: string,
): Observable<WorkspaceRoles> {
  const url = `${origin}/api/v3/workspace/${workspaceId}/roles`;

  return ajax.getJSON<WorkspaceRoles>(url, {
    Authorization: `Bearer ${token}`,
  });
}

/** Remove workspace permanently **/
function deleteWorkspace({
  origin,
  token,
  workspaceId,
  password,
}: BaseRequest<{ workspaceId: string; password?: string }>) {
  const url = `${origin}/api/v3/workspace/${workspaceId}`;

  return ajax.delete(url, {
    Authorization: `Bearer ${token}`,
    "X-Hoylu-Workspace-Password": password,
  });
}

/**
 * Set or remove the password for the specified workspace.
 *
 * @param arg.currentPassword The current workspace password.
 * @param arg.newPassword The new password for the workspace. If null or empty, the password will be removed.
 */
function patchWorkspacePassword({
  currentPassword,
  newPassword,
  origin,
  token,
  workspaceId,
}: BaseRequest<{
  currentPassword: string | undefined;
  newPassword: string | null;
  workspaceId: string;
}>) {
  const url = `${origin}/api/v3/workspace/${workspaceId}/password`;

  return ajax.patch(url, JSON.stringify(newPassword), {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...(currentPassword && {
      "X-Hoylu-Workspace-Password": currentPassword,
    }),
  });
}

/**
 * Set the name for the specified workspace.
 */
function patchWorkspaceName({
  currentPassword,
  origin,
  token,
  workspaceId,
  workspaceName,
}: BaseRequest<{
  currentPassword?: string;
  workspaceId: string;
  workspaceName: string;
}>) {
  const url = `${origin}/api/v3/workspace/${workspaceId}/name`;

  return ajax.patch(url, JSON.stringify(workspaceName), {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...(currentPassword && {
      "X-Hoylu-Workspace-Password": currentPassword,
    }),
  });
}

/**
 * Update the labels for the specified workspace.
 */
function patchWorkspaceLabels({
  labels,
  origin,
  token,
  workspaceId,
  currentPassword,
}: BaseRequest<{
  workspaceId: string;
  labels: Label[];
  currentPassword?: string;
}>) {
  const url = `${origin}/api/v3/workspace/${workspaceId}/labels`;

  return ajax.patch(url, JSON.stringify(labels), {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...(currentPassword && {
      "X-Hoylu-Workspace-Password": currentPassword,
    }),
  });
}

/**
 * Set the global access permission for the specified workspace.
 */
function patchWorkspaceGlobalAccess({
  globalAccess,
  origin,
  token,
  workspaceId,
  workspacePassword,
}: BaseRequest<{
  workspaceId: string;
  globalAccess: GlobalAccessType;
  workspacePassword?: string;
}>) {
  const url = `${origin}/api/v3/workspace/${workspaceId}/globalaccess`;

  return ajax.patch(url, JSON.stringify(globalAccess), {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...(workspacePassword && {
      "X-Hoylu-Workspace-Password": workspacePassword,
    }),
  });
}

// TONNAGE ENDPOINTS

function getMyWorkspacesTonnage(
  origin: string,
  token: string,
): Observable<UserWorkspaceTonnageResponse> {
  const url = `${origin}/api/v3/tonnage/users/my`;

  return ajax.getJSON<UserWorkspaceTonnageResponse>(url, {
    Authorization: `Bearer ${token}`,
  });
}

function getUserWorkspacesTonnageById(
  origin: string,
  token: string,
  userId: string,
): Observable<UserWorkspaceTonnageResponse> {
  const url = `${origin}/api/v3/tonnage/users/${userId}`;

  return ajax.getJSON<UserWorkspaceTonnageResponse>(url, {
    Authorization: `Bearer ${token}`,
  });
}

function recalculateUserTonnage(
  origin: string,
  token: string,
  userId: string,
  force?: boolean,
): Observable<UserWorkspaceTonnageResponse> {
  let url = `${origin}/api/v3/tonnage/users/${userId}/recalculate`;

  if (force) {
    url += `?force=${force}`;
  }

  return putJSON<undefined, UserWorkspaceTonnageResponse>(
    url,
    undefined,
    { Authorization: `Bearer ${token}` },
    true,
  );
}

export const documentIdv3Requests = {
  createWorkspace,
  deleteProjectsProjectWorkspace,
  deleteWorkspace,
  duplicateWorkspace,
  getDashboardHiddenWorkspacesPaged,
  getDashboardLabels,
  getDashboardWorkspacesPaged,
  getMyWorkspacesTonnage,
  getProjectsMy,
  getProjectsMyAssignable,
  getProjectUsers,
  getProjectWorkspaces,
  getRawWorkspaceMetadata,
  getUserWorkspacesTonnageById,
  getWorkspaceRoles,
  patchWorkspaceGlobalAccess,
  patchWorkspaceLabels,
  patchWorkspaceName,
  patchWorkspacePassword,
  putProjectsProjectWorkspaces,
  recalculateUserTonnage,
  removeFromMyWorkspaces,
  removeUsersFromWorkspacePermissions,
  setDashboardWorkspaceVisibility,
  upsertWorkspaceUserPermissions,
};
